// frontend/src/components/admin/ProspectionManager.jsx

import { useState } from 'react';
import {
  FileText, Users, BarChart2, Download, Send,
  CheckCircle2, AlertCircle, Tag, ArrowRight,
  Sparkles, ChevronRight, Layers,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ── Icône Presentation inline ──────────────────────────────────────────────
const PresentationIcon = ({ size = 20, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d="M2 3h20" /><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
    <path d="m7 21 5-5 5 5" />
  </svg>
);

// ============================================================================
// CONFIG FICHES — 2 fiches (Présentation + Licences & Tarifs fusionnées)
// ============================================================================
const FICHES = [
  {
    key:         'presentation',
    titre:       'Fiche Présentation',
    sous_titre:  'Vue générale de HoraPilot',
    description: "Présente l'application, les modules clés et les avantages concurrentiels. Idéale pour un premier contact.",
    Icon:        PresentationIcon,
    color:       '#448696',
    colorLight:  'rgba(68,134,150,0.10)',
    colorBorder: 'rgba(68,134,150,0.22)',
    pages:       '1 page · A4',
    usage:       'Premier contact · Réseaux sociaux · Email découverte',
    contenu: [
      'Logo + coordonnées DANICED GROUP',
      'Bloc héro HoraPilot',
      'Les modules phares',
      'Les avantages clés',
      'Call-to-action avec contact',
    ],
    hasProspect: false,
  },
  {
    key:         'licences_tarifs',
    titre:       'Fiche Licences & Tarifs',
    sous_titre:  'Comparatif + grille tarifaire personnalisée',
    description: "Tableau comparatif des 3 licences avec modules inclus (issus de la grille), prix mensuel, frais de mise en place et zone signature.",
    Icon:        Layers,
    color:       '#8b5cf6',
    colorLight:  'rgba(139,92,246,0.10)',
    colorBorder: 'rgba(139,92,246,0.22)',
    pages:       '1 page · A4',
    usage:       'Après premier contact · Proposition commerciale · Pré-devis',
    contenu: [
      '3 colonnes Basique / Standard / Premium',
      'Modules inclus par licence (grille dynamique)',
      'Limites employés et utilisateurs',
      'Prix mensuel + frais de mise en place',
      'Personnalisable au nom du prospect',
      'Zone signature client + DANICED',
    ],
    hasProspect: true,
    badge: 'Données automatiques',
  },
];

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const ProspectionManager = ({ onNavigate }) => {
  const t = useTheme();

  const [loadingMap,      setLoadingMap]      = useState({});
  const [successMap,      setSuccessMap]      = useState({});
  const [error,           setError]           = useState('');
  const [prospectId,      setProspectId]      = useState('');
  const [prospects,       setProspects]       = useState([]);
  const [prospectsLoaded, setProspectsLoaded] = useState(false);

  // Charger prospects au focus du select (lazy)
  const loadProspects = async () => {
    if (prospectsLoaded) return;
    try {
      const res = await superAdminApi.prospects.getAll();
      if (res.success) setProspects(res.data || []);
      setProspectsLoaded(true);
    } catch { /* silencieux */ }
  };

  // Télécharger une fiche
  const handleDownload = async (fiche) => {
    setLoadingMap(m => ({ ...m, [fiche.key]: true }));
    setError('');
    try {
      const pid = fiche.hasProspect && prospectId ? parseInt(prospectId) : null;
      await superAdminApi.prospection.downloadFiche(fiche.key, pid);
      setSuccessMap(m => ({ ...m, [fiche.key]: true }));
      setTimeout(() => setSuccessMap(m => ({ ...m, [fiche.key]: false })), 2500);
    } catch {
      setError(`Erreur lors de la génération de la fiche "${fiche.titre}"`);
    } finally {
      setLoadingMap(m => ({ ...m, [fiche.key]: false }));
    }
  };

  const nav = (key) => onNavigate && onNavigate(key);

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* ── HEADER ── */}
      <div>
        <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 5px' }}>Prospection</h1>
        <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>
          Générez des fiches PDF professionnelles pour présenter HoraPilot à vos prospects
        </p>
      </div>

      {/* ── ERREUR ── */}
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {/* ── WORKFLOW BANNER ── */}
      <div style={{ padding: '14px 18px', borderRadius: 14, background: t.primarySoft, border: `1px solid ${t.primaryBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Sparkles size={14} style={{ color: t.primary }} />
          <span style={{ fontSize: 12, fontWeight: 800, color: t.primary }}>Workflow commercial recommandé</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          {[
            { label: '① Fiche Présentation',    sub: 'Premier contact',  nav: null        },
            { label: '② Fiche Licences & Tarifs',sub: 'Si intéressé',    nav: null        },
            { label: '③ Ajouter prospect',       sub: 'Dans le CRM',     nav: 'prospects' },
            { label: '④ Créer un devis',         sub: 'Proposition',     nav: 'devis'     },
            { label: '⑤ Générer contrat',        sub: 'À la signature',  nav: 'contrats'  },
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
              <div
                onClick={() => step.nav && nav(step.nav)}
                style={{
                  padding: '6px 11px', borderRadius: 8,
                  background: step.nav ? t.primary + '18' : t.bgCard,
                  border: `1px solid ${step.nav ? t.primary + '45' : t.border}`,
                  cursor: step.nav ? 'pointer' : 'default',
                }}>
                <p style={{ fontSize: 10, fontWeight: 800, color: step.nav ? t.primary : t.text, margin: 0 }}>{step.label}</p>
                <p style={{ fontSize: 9, color: t.textMuted, margin: 0 }}>{step.sub}</p>
              </div>
              {i < 4 && <ChevronRight size={12} style={{ color: t.textMuted, flexShrink: 0, margin: '0 2px' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── CARTES FICHES — 2 colonnes ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {FICHES.map(fiche => {
          const { Icon } = fiche;
          const isLoading = loadingMap[fiche.key];
          const isSuccess = successMap[fiche.key];

          return (
            <div key={fiche.key} style={{ borderRadius: 16, background: t.bgCard,
              border: `1px solid ${t.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

              {/* Bande top */}
              <div style={{ height: 4, background: fiche.color }} />

              {/* Corps */}
              <div style={{ padding: '18px 18px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 13 }}>

                {/* Icône + titre + badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: fiche.colorLight, border: `1px solid ${fiche.colorBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={18} style={{ color: fiche.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 2px' }}>{fiche.titre}</p>
                      {fiche.badge && (
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                          background: fiche.colorLight, color: fiche.color,
                          border: `1px solid ${fiche.colorBorder}`,
                        }}>
                          ✦ {fiche.badge}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{fiche.sous_titre}</p>
                  </div>
                </div>

                {/* Description */}
                <p style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.65, margin: 0 }}>
                  {fiche.description}
                </p>

                {/* Contenu */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {fiche.contenu.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11, color: t.textSecondary }}>
                      <span style={{ color: fiche.color, fontWeight: 900, flexShrink: 0 }}>✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Méta */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <FileText size={10} />{fiche.pages}
                  </span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>·</span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>{fiche.usage}</span>
                </div>

                {/* Select prospect */}
                {fiche.hasProspect && (
                  <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 12 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, margin: '0 0 6px' }}>
                      Personnaliser pour un prospect (optionnel)
                    </p>
                    <select
                      value={prospectId}
                      onChange={e => setProspectId(e.target.value)}
                      onFocus={loadProspects}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8,
                        background: t.bgInput, border: `1px solid ${t.border}`,
                        color: prospectId ? t.text : t.textMuted, fontSize: 12, outline: 'none' }}>
                      <option value="">— Générique (sans nom) —</option>
                      {prospects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nom}{p.contact ? ` · ${p.contact}` : ''}
                        </option>
                      ))}
                    </select>

                    {prospectsLoaded && prospects.length === 0 && (
                      <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 8,
                        background: t.primarySoft, border: `1px solid ${t.primaryBorder}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: t.textSecondary }}>Aucun prospect enregistré</span>
                        <button onClick={() => nav('prospects')}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                            fontWeight: 700, color: t.primary, background: 'none', border: 'none',
                            cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}>
                          Créer un prospect <ArrowRight size={11} />
                        </button>
                      </div>
                    )}

                    {/* Info : données auto depuis la grille */}
                    <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 8,
                      background: fiche.colorLight, border: `1px solid ${fiche.colorBorder}`,
                      display: 'flex', alignItems: 'center', gap: 7 }}>
                      <Layers size={12} style={{ color: fiche.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, color: fiche.color, fontWeight: 600 }}>
                        Modules et tarifs chargés automatiquement depuis la configuration
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Bouton télécharger */}
              <div style={{ padding: '0 18px 18px' }}>
                <button
                  onClick={() => handleDownload(fiche)}
                  disabled={isLoading}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 10, border: 'none',
                    background: isSuccess ? '#22c55e' : fiche.colorLight,
                    color:      isSuccess ? '#fff'    : fiche.color,
                    fontSize: 12, fontWeight: 800,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.75 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    transition: 'all 0.2s',
                    outline: `1.5px solid ${isSuccess ? '#22c55e' : fiche.colorBorder}`,
                  }}>
                  {isLoading ? (
                    <>
                      <span style={{ width: 13, height: 13, border: `2px solid ${fiche.color}40`,
                        borderTopColor: fiche.color, borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                      Génération…
                    </>
                  ) : isSuccess ? (
                    <><CheckCircle2 size={14} /> Téléchargé !</>
                  ) : (
                    <><Download size={14} /> Télécharger la fiche</>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── CTA BAS DE PAGE ── */}
      <div style={{ padding: '18px 22px', borderRadius: 16, background: t.bgCard,
        border: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 3px' }}>
            Prêt à créer votre premier devis ?
          </p>
          <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
            Ajoutez d'abord un prospect dans le CRM, puis créez-lui un devis personnalisé.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={() => nav('prospects')}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
              borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`,
              fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Users size={13} /> Gérer les prospects
          </button>
          <button onClick={() => nav('devis')}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
              borderRadius: 10, background: t.primary, color: '#fff', border: 'none',
              fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Send size={13} /> Créer un devis <ArrowRight size={12} />
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default ProspectionManager;